import React, {
  useState,
  useContext,
  useRef,
  useEffect,
  useCallback,
} from "react";
import { NavLink } from "react-router-dom";
import Moment from "react-moment";
import moment from "moment";
import { useReactToPrint } from "react-to-print";

//pages & includes
import AccountsSidebar from "../AccountsSidebar";
import AccountPrint from "./AccountPrint";
import ViewAccountSheet from "./ViewAccountSheet";

//functions
import { getCookie } from "../../../../../functions/Functions";

import { useTranslation } from "react-i18next";
import { MdDelete } from "react-icons/md";

//axios and base url
import axios from "axios";
import { BASE_URL } from "../../../../../BaseUrl";

//3rd party packages
import { Helmet } from "react-helmet";
import { confirmAlert } from "react-confirm-alert";

import "react-confirm-alert/src/react-confirm-alert.css";
import "react-datepicker/dist/react-datepicker.css";

//context consumer
import { UserContext } from "../../../../../contexts/User";
import DataTable from "../../../../common/DataTable";
import { ToastSuccess, ToastError } from "../../../../../functions/ToastShow";

const CashReceipt = () => {
  const { t } = useTranslation();

  const { authUserInfo } = useContext(UserContext);

  const [cashReceiptList, setCashReceiptList] = useState(null);
  const [cashReceiptListForSearch, setCashReceiptListForSearch] =
    useState(null);
  const [isFetchingData, setIsFetchingData] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  //search result
  let [searchedBPList, setSearchedBPList] = useState({
    list: null,
    searched: false,
  });

  const [multiCashReceipt, setMultiCashReceipt] = useState({
    cashReceipt: null,
    is_post: 1,
  });

  const toggleSelectAll = (e) => {
    e.stopPropagation();
    let updated = { ...multiCashReceipt.cashReceipt };
    const currentList =
      (searchedBPList?.searched && searchedBPList?.list) ||
      cashReceiptList?.data ||
      [];
    if (e.target.checked) {
      currentList.forEach((item) => {
        if (!item.is_post) {
          updated[item.id] = item.id;
        }
      });
    } else {
      currentList.forEach((item) => {
        if (updated[item.id]) {
          delete updated[item.id];
        }
      });
    }

    setMultiCashReceipt((curr) => ({
      ...curr,
      cashReceipt: updated,
    }));
  };

  const toggleSelect = (id) => {
    let updated = { ...multiCashReceipt.cashReceipt };
    if (updated[id]) {
      delete updated[id];
      if (document.getElementById("multi_cash_check")) {
        document.getElementById("multi_cash_check").checked = false;
      }
    } else {
      updated[id] = id;
    }
    setMultiCashReceipt((curr) => ({
      ...curr,
      cashReceipt: updated,
    }));
  };

  //multi post
  const handleSaveMultiCashReceipt = async (e) => {
    e.preventDefault();
    if (
      !multiCashReceipt?.cashReceipt ||
      !Object.values(multiCashReceipt?.cashReceipt)?.length
    ) {
      ToastError({
        title: t("Please select item to post"),
      });
      return false;
    }
    setActionLoading(true);

    const url = BASE_URL + "/settings/cash-receipt-post";
    const formData = {
      id: multiCashReceipt.cashReceipt
        ? Object.values(multiCashReceipt.cashReceipt)
        : [],
      is_post: multiCashReceipt.is_post ?? 1,
    };
    try {
      const res = await axios.post(url, formData, {
        headers: { Authorization: `Bearer ${getCookie()}` },
      });
      if (res.data) {
        setActionLoading(false);
        setCashReceiptList(res.data[0]);
        setCashReceiptListForSearch(res.data[1]);
        setSearchedBPList({
          ...searchedBPList,
          list: res.data[1],
        });
        setMultiCashReceipt({
          cashReceipt: null,
          is_post: 1,
        });
        ToastSuccess({
          title: t("Cash receipt has been posted"),
        });
        if (document.getElementById("multi_cash_check")) {
          document.getElementById("multi_cash_check").checked = false;
        }
      }
    } catch (error) {
      setActionLoading(false);
      if (error?.response?.data && error.response.data?.status == 0) {
        ToastError({
          title: t(error.response.data.message),
        });
        return false;
      }
      if (error?.response?.data?.message) {
        ToastError({
          title: t(error.response.data.message),
        });
        return false;
      }
      ToastError({
        title: t("Please try again"),
      });
    }
  };

  const componentRef = useRef("");
  const searchInputRef = useRef(null);

  //print satate
  const [bankPayment, setBankPayment] = useState({
    doc_date: new Date(),
    transactionAccount: "",
    doc_no: null,
    branch_name: "",
    customer_name: "",
    remarks: "",
  });

  const [accountLedger, setAccountLedger] = useState({
    modal: false,
    ledger: null,
  });

  //Totals of rows
  const [totalRowAmount, setTotalRowAmount] = useState(0);
  const [totalRowWhtAmount, setTotalRowWhtAmount] = useState(0);
  const [totalRowTaxAmount, setTotalRowTaxAmount] = useState(0);
  const [totalRowNetAmount, setTotalRowNetAmount] = useState(0);
  const [newRow, setNewRow] = useState([]);

  useEffect(() => {
    getBPReciptList(1);
  }, []);

  const getBPReciptList = async (pageNo) => {
    setIsFetchingData(true);

    const url = BASE_URL + "/settings/cash-receipt?page=" + pageNo;

    try {
      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${getCookie()}` },
      });

      setCashReceiptList(res.data[0]);
      setCashReceiptListForSearch(res.data[1]);
      setIsFetchingData(false);
    } catch (error) {
      setIsFetchingData(false);
    }
  };

  const handleOnlyPrint = useReactToPrint({
    content: () => componentRef.current,
    pageStyle: `
        @page {
          size: landscape;
          margin: 15px;
        }
        body {
          -webkit-print-color-adjust: exact;
        }
      `,
  });

  const handlePrint = async (item) => {
    setIsFetchingData(true);
    const url = BASE_URL + "/settings/cash-receipt/" + item.id;
    try {
      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${getCookie()}` },
      });
      const dateFormat = new Date(res.data.document_date);
      let cashReceiptDetail = [];

      if (res.data.cashreceiptdetail) {
        res.data.cashreceiptdetail.forEach((item) => {
          cashReceiptDetail.push({
            level3_name: item.coaname?.level3_name,
            id: item.id,
            account_name: item.account_name,
            cheque_date: item.cheque_date,
            cheque_no: item.cheque_no,
            amount: item.amount,
            remarks: item.remarks,
            other_tax_amount: item.other_tax_amount,
            wht_amount: item.wht_amount,
            net_amount: item.net_amount,
          });
        });
      }

      setNewRow(cashReceiptDetail);

      setBankPayment({
        doc_date: dateFormat,
        doc_no: res.data?.document_identity,
        branch_name: res?.data?.branch?.name,
        customer_name: res?.data?.customer?.name,
        transactionAccount: res?.data?.transaction_name?.level3_name,
        remarks: res?.data?.remarks,
      });

      setTotalRowAmount(res?.data?.total_amount);
      setTotalRowNetAmount(res?.data?.total_net_amount);
      setTotalRowTaxAmount(res?.data?.total_other_tax_amount);
      setTotalRowWhtAmount(res?.data?.total_wht_amount);
      setTimeout(() => {
        handleOnlyPrint();
      }, 0);
      setIsFetchingData(false);
      //   setDataToLocal(url, res);
    } catch (error) {
      setIsFetchingData(false);
    }
  };

  const handleViewLedger = async (id) => {
    setIsFetchingData(true);
    const url =
      BASE_URL + "/settings/check-financial-ledger-view/" + id + "/CR";
    try {
      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${getCookie()}` },
      });
      if (res.data) {
        setAccountLedger({
          modal: true,
          ledger: res.data,
        });
      }
      setIsFetchingData(false);
    } catch (error) {
      setIsFetchingData(false);
    }
  };

  //delete account here
  const handleDeleteAccount = (slug) => {
    setIsFetchingData(true);
    const bplUrl = BASE_URL + `/settings/cash-receipt/${slug}`;
    return axios
      .delete(bplUrl, {
        headers: { Authorization: `Bearer ${getCookie()}` },
      })
      .then((res) => {
        setCashReceiptList(res.data[0]);
        setCashReceiptListForSearch(res.data[1]);
        setSearchedBPList({
          ...searchedBPList,
          list: res.data[1],
        });
        setIsFetchingData(false);
        ToastSuccess({
          title: t("Cash receipt has been deleted successfully"),
        });
      })
      .catch(() => {
        setIsFetchingData(false);
        ToastError({ title: t("Please try again") });
      });
  };

  //search account here
  const handleSearch = (e) => {
    if (document.getElementById("multi_cash_check")) {
      document.getElementById("multi_cash_check").checked = false;
    }
    setMultiCashReceipt({
      cashReceipt: null,
      is_post: 1,
    });
    let searchInput = e.target.value.toLowerCase();
    if (searchInput.length === 0) {
      setSearchedBPList({ ...searchedBPList, searched: false });
    } else {
      let searchedList = cashReceiptListForSearch.filter((item) => {
        let lowerCaseItemIdentity = item.document_identity.toLowerCase();
        let lowerCaseItemTotal = item.total_net_amount.toLowerCase();
        let lowerCaseItemDate = moment(item.document_date)
          .format("LL")
          .toLowerCase();
        const lowerCaseSupplier =
          item.supplier && item?.supplier?.name.toLowerCase();
        return (
          lowerCaseItemIdentity.includes(searchInput) ||
          lowerCaseItemTotal.includes(searchInput) ||
          (lowerCaseSupplier && lowerCaseSupplier.includes(searchInput)) ||
          (lowerCaseItemDate && lowerCaseItemDate.includes(searchInput))
        );
      });
      setSearchedBPList({
        ...searchedBPList,
        list: searchedList,
        searched: true,
      });
    }
  };

  //delete confirmation modal of account
  const handleDeleteConfirmation = (slug) => {
    confirmAlert({
      customUI: ({ onClose }) => {
        return (
          <div className="card card-body">
            <h1>{t("Are you sure?")}</h1>
            <p className="text-center">{t("You want to delete this?")}</p>
            <div className="d-flex justify-content-center">
              <button
                className="btn btn-primary rounded"
                onClick={() => {
                  handleDeleteAccount(slug);
                  onClose();
                }}
              >
                {t("Yes, delete it!")}
              </button>
              <button
                className="btn btn-success ml-2 px-3 rounded"
                onClick={onClose}
              >
                {t("No")}
              </button>
            </div>
          </div>
        );
      },
    });
  };

  const handlePostApi = async (item) => {
    setActionLoading(true);

    const url = BASE_URL + "/settings/cash-receipt-post";

    let formData = new FormData();
    formData.append("id", item.id);
    formData.append("is_post", item.is_post ? 0 : 1);

    try {
      const res = await axios.post(url, formData, {
        headers: { Authorization: `Bearer ${getCookie()}` },
      });

      if (res.data) {
        setCashReceiptList(res.data[0]);
        setCashReceiptListForSearch(res.data[1]);
        setSearchedBPList({
          ...searchedBPList,
          list: res.data[1],
        });
        setMultiCashReceipt({
          cashReceipt: null,
          is_post: 1,
        });
        if (document.getElementById("multi_cash_check")) {
          document.getElementById("multi_cash_check").checked = false;
        }
        ToastSuccess({
          title: t(
            item.is_post
              ? "Cash receipt has been unpost"
              : "Cash receipt has been post"
          ),
        });
      }

      setActionLoading(false);
    } catch {
      setActionLoading(false);
      ToastError({ title: t("Please try again") });
    }
  };

  //unpost confirmation modal
  const handleUnPostConfirmation = (item) => {
    confirmAlert({
      customUI: ({ onClose }) => {
        return (
          <div className="card card-body px-5">
            <h3>{t("Are you sure?")}</h3>
            <p className="text-center">{t("You want to un post this?")}</p>
            <div className="d-flex justify-content-center">
              <button
                className="btn btn-primary"
                onClick={() => {
                  handlePostApi(item);
                  onClose();
                }}
              >
                {t("Yes, unpost it!")}
              </button>
              <button className="btn btn-success ml-2 px-3" onClick={onClose}>
                {t("No")}
              </button>
            </div>
          </div>
        );
      },
    });
  };

  const COLUMNS = [
    { label: "S/L", render: (item, i) => i + 1 },
    {
      labelRender: () => {
        return (
          <div className="d-flex justify-content-center align-items-center">
            <button
              className="btn btn-sm btn-dark mr-2"
              onClick={(e) => handleSaveMultiCashReceipt(e)}
              disabled={actionLoading}
            >
              <i className="fa fa-check-square-o"></i>
            </button>
            <input
              id="multi_cash_check"
              type="checkbox"
              className="form-check-input table-text"
              onChange={(e) => {
                toggleSelectAll(e);
              }}
            />
          </div>
        );
      },
      render: (item) => {
        return (
          <input
            disabled={item.is_post}
            id={item.id}
            type="checkbox"
            className="form-check-input table-text"
            checked={
              multiCashReceipt?.cashReceipt &&
              Boolean(multiCashReceipt.cashReceipt[item.id])
            }
            onClick={(e) => e.stopPropagation()}
            onChange={() => {
              toggleSelect(item.id);
            }}
          />
        );
      },
    },
    { label: "Document No", key: "document_identity" },
    {
      label: "Document Date",
      render: (item) => <Moment format="LL">{item.document_date}</Moment>,
    },
    { label: "Branch", render: (item) => item.branch?.name },
    { label: "Customer", render: (item) => item.customer?.name || "-" },
    { label: "Total", key: "total_net_amount" },
    {
      label: "Action",
      render: (item) => (
        <div className="d-flex justify-content-center">
          {item.is_post !== 1 && (
            <>
              <NavLink
                title="Edit"
                className="btn btn-primary ml-2 rounded-sm del-btn"
                to={`/dashboard/account/transaction/cash-receipt-edit/${item.id}`}
              >
                <i className="fa fa-pencil edit-icon" aria-hidden="true"></i>
              </NavLink>
              <button
                title="Delete"
                className="btn btn-danger ml-2 rounded-sm del-btn"
                onClick={() => handleDeleteConfirmation(item.id)}
              >
                <MdDelete className="del-icon" />
              </button>
            </>
          )}
          <button
            title="Print"
            className="btn btn-secondary ml-2 rounded-sm del-btn"
            onClick={() => handlePrint(item)}
          >
            <i className="fa fa-print edit-icon"></i>
          </button>
          {item.is_post !== 1 ? (
            <button
              className="btn btn-dark ml-2 rounded-sm del-btn"
              title="Post Cash Receipt"
              style={{ padding: "1px 0px 0px 1px" }}
              disabled={actionLoading}
              onClick={() => handlePostApi(item)}
            >
              <i className="fa fa-check-square-o" aria-hidden="true"></i>
            </button>
          ) : null}
          {authUserInfo?.details?.user_type === "superAdmin" &&
          item.is_post === 1 ? (
            <button
              className="btn btn-warning ml-2 rounded-sm del-btn"
              title="Unpost Cash Receipt"
              style={{ padding: "1px 0px 0px 1px" }}
              disabled={actionLoading}
              onClick={() => handleUnPostConfirmation(item)}
            >
              <i className="fa fa-check-square-o" aria-hidden="true"></i>
            </button>
          ) : null}
          {item.is_post === 1 && (
            <button
              title="View ledger"
              className="btn btn-success ml-2 rounded-sm del-btn"
              onClick={() => handleViewLedger(item.id)}
            >
              <i className="fa fa-eye"></i>
            </button>
          )}
        </div>
      ),
    },
  ];

  const handleFetchData = (page) => {
    setMultiCashReceipt({
      cashReceipt: null,
      is_post: 1,
    });
    if (document.getElementById("multi_cash_check")) {
      document.getElementById("multi_cash_check").checked = false;
    }
    getBPReciptList(page);
  };

  const getListData = useCallback(() => {
    if (searchedBPList.searched) {
      return searchedBPList.list;
    }

    return cashReceiptList?.data || [];
  }, [searchedBPList, cashReceiptList]);

  const onClearSearch = () => {
    setSearchedBPList({ ...searchedBPList, searched: false });
    if (searchInputRef.current) {
      searchInputRef.current.value = "";
    }
    setMultiCashReceipt({
      cashReceipt: null,
      is_post: 1,
    });
    if (document.getElementById("multi_cash_check")) {
      document.getElementById("multi_cash_check").checked = false;
    }
  };

  return (
    <>
      <Helmet>
        <title>{t("Cash Receipt List")}</title>
      </Helmet>

      {/* main body */}
      <main id="main" data-simplebar>
        <div className="container-fluid">
          <div className="row t-mt-10 gx-2">
            {/* left Sidebar */}
            <div className="col-lg-2 col-xxl-2 t-mb-30 mb-lg-0">
              <AccountsSidebar />
            </div>
            {/* left Sidebar ends */}

            {/* Rightbar contents */}
            <div className="col-lg-10 col-xxl-10 t-mb-30 mb-lg-0">
              <div className="">
                <div key="fragment3">
                  {/* {Table Header} */}
                  <div className="t-bg-white">
                    <div className="row mx-3 gx-2 align-items-center t-pt-15 t-pb-15">
                      <div className="col-md-6 col-lg-5 t-mb-15 mb-md-0">
                        <ul className="t-list fk-breadcrumb">
                          <li className="fk-breadcrumb__list">
                            <span className="t-link fk-breadcrumb__link text-capitalize">
                              {!searchedBPList.searched
                                ? t("Cash Receipt List")
                                : t("Search Result")}
                            </span>
                          </li>
                        </ul>
                      </div>
                      <div className="col-md-6 col-lg-7">
                        <div className="row gx-3 align-items-center">
                          {/* Search group */}
                          <div className="col-md-9 t-mb-15 mb-md-0">
                            <div className="input-group">
                              <div className="form-file">
                                <input
                                  type="text"
                                  className="form-control border-0 form-control--light-1 rounded-left"
                                  placeholder={t("Search") + ".."}
                                  onChange={handleSearch}
                                  disabled={isFetchingData}
                                  ref={searchInputRef}
                                />
                              </div>
                              <button
                                className="btn btn-primary rounded-right"
                                type="button"
                              >
                                <i
                                  className="fa fa-search"
                                  aria-hidden="true"
                                ></i>
                              </button>
                            </div>
                          </div>

                          {/* Add group modal trigger button */}
                          <div className="col-md-3 text-md-right">
                            <NavLink
                              to="/dashboard/account/transaction/cash-receipt-add"
                              className="btn btn-primary xsm-text text-uppercase btn-lg btn-block rounded"
                            >
                              {t("add new")}
                            </NavLink>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <DataTable
                    loading={actionLoading || isFetchingData}
                    data={getListData()}
                    columns={COLUMNS}
                    emptyText="No data available"
                    pagination={{
                      isPageLoading: actionLoading,
                      isPagination: true,
                      pageCount: cashReceiptList?.last_page,
                      currentPage: cashReceiptList?.current_page,
                      from: cashReceiptList?.from,
                      to: cashReceiptList?.to,
                      total: cashReceiptList?.total,
                    }}
                    handleFetchData={handleFetchData}
                    searchPayload={{
                      isSearched: searchedBPList.searched,
                      onClearSearch,
                    }}
                  />
                </div>
              </div>
            </div>
            {/* Rightbar contents end*/}
          </div>
        </div>
      </main>
      {/* main body ends */}
      <AccountPrint
        name="Cash Receipt"
        newRow={newRow}
        bankPayment={bankPayment}
        totalRowAmount={totalRowAmount}
        totalRowWhtAmount={totalRowWhtAmount}
        totalRowTaxAmount={totalRowTaxAmount}
        totalRowNetAmount={totalRowNetAmount}
        AccountPrint={componentRef}
      />
      <ViewAccountSheet
        accountLedger={accountLedger}
        setAccountLedger={setAccountLedger}
      />
    </>
  );
};

export default CashReceipt;
